import * as THREE from 'three';
import FBXLoader from 'three-fbxloader-offical';
import cloneDeep from 'lodash.clonedeep';
import OrbitControls from 'sunzi-three-orbitcontrols';
import Zen3d, { Zen3dOptions, Zen3dColorName } from '@/utils/zen3d';
import { SockType } from './new-sock';
import sockConfig from './new-sock-config';

type TextureType = 'normal' | 'shadow' | 'annex';

class Sock3d extends Zen3d {
  public bumpTexture: THREE.Texture;
  public shadowTexture: THREE.Texture;
  public annexTexture: { [name: string]: THREE.Texture };
  private productType: SockType;

  constructor(options: Zen3dOptions, productType: SockType) {
    super(options);
    this.productType = productType;
    this.autoRotate = false;

    const cameraLookAt = sockConfig[productType].cameraLookat;
    this.camera.lookAt(new THREE.Vector3(...cameraLookAt));
    this.orbitControls.target.set(...cameraLookAt);

    this.bumpTexture = new THREE.Texture();
    this.shadowTexture = new THREE.Texture();
    this.annexTexture = {};

    if (productType === SockType.SOCK) {
      this._sockInit();
    } else {
      this._boxerInit();
    }
  }

  /** 内裤初始化 */
  private _boxerInit() {
    const rectLight = new THREE.RectAreaLight(0xffffff, 0.9, 1000, 1000);
    rectLight.position.set(0, 100, 200);
    rectLight.lookAt(0, 0, 0);
    const rectLight2 = rectLight.clone();
    rectLight2.position.set(0, 100, -200);
    rectLight2.lookAt(0, 0, 0);
    this.scene.add(rectLight);
    this.scene.add(rectLight2);

    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.shadowMap.autoUpdate = true;
    this.renderer.physicallyCorrectLights = true;
  }

  /** 袜子初始化 */
  private _sockInit() {
    const directionalLight = new THREE.DirectionalLight(0xffffff, 8);
    directionalLight.position.set(50, 100, 80);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 1024 * 2;
    directionalLight.shadow.mapSize.height = 1024 * 2;
    directionalLight.shadow.camera.near = 0.1;
    directionalLight.shadow.camera.far = 500;
    directionalLight.shadow.camera.left = -100;
    directionalLight.shadow.camera.right = 100;
    directionalLight.shadow.camera.top = 100;
    directionalLight.shadow.camera.bottom = -100;
    this.scene.add(directionalLight);

    this.scene.add(new THREE.HemisphereLight(0xffffff, 0xffffff, 1));

    const dL2 = directionalLight.clone();
    dL2.position.set(0, 0, -1);
    dL2.intensity = 1;
    this.scene.add(dL2);

    const dL3 = directionalLight.clone();
    dL3.position.set(0, -1, 0);
    dL3.intensity = 1;
    this.scene.add(dL3);

    this.scene.add(new THREE.HemisphereLight(0xffffff, 0xffffff, 5));

    this.bumpTexture = new THREE.Texture();
    this.shadowTexture = new THREE.Texture();
    this.annexTexture = {};

    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.shadowMap.autoUpdate = true;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.physicallyCorrectLights = true;
  }

  /** 加载fbx文件 */
  public async loadFBX(pathFbx: string) {
    return new Promise<void>(resolve => {
      if (this.object) resolve();
      new FBXLoader().load(pathFbx, (object: any) => {
        this.object = object;
        // 如果是内裤把模型稍微调高一点
        if (this.productType === SockType.BOXER) {
          const meshPosition = sockConfig[this.productType].initialObject;
          object.position.set(meshPosition.x, meshPosition.y, meshPosition.z);
        }
        this.scene.add(object);
        object.traverse((child: any) => {
          child.castShadow = true;
          child.receiveShadow = true;
          this.objects[child.name.toLowerCase()] = child;
          resolve();
        });
      });
    });
  }

  /** 上传贴图 */
  public async loadTexture(source: string, type: TextureType, name?: string) {
    return new Promise<void>(resolve => {
      this.textureloader.load(source, (texture) => {
        if (type === 'normal') {
          this.bumpTexture = texture;
        } else if (type === 'shadow') {
          this.shadowTexture = texture;
          const geometry = new THREE.PlaneGeometry(50, 50);
          const material = new THREE.MeshBasicMaterial({
              map: texture,
              color: new THREE.Color(0x111111),
              transparent: true,
          });
          const mesh = new THREE.Mesh(geometry, material);
          mesh.rotation.set(Math.PI / 2, Math.PI, Math.PI / 6);
          mesh.position.set(7.5, 0, 4);
          this.scene.add(mesh);
        } else if (type === 'annex' && name) {
          this.annexTexture[name] = texture;
        }
        resolve();
      });
    });
  }

  /** 渲染到dom节点 */
  public async render(container: HTMLElement) {
    this.camera.aspect = container.clientWidth / container.clientHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(this.renderer.domElement);
    this.animate();
  }

  /** 更新纹理 */
  public async updateTexture(source: string) {
    return new Promise<void>(resolve => {
      const image = new Image();
      this.texture.image = image;
      image.onload = () => {
        this.objectNames?.map((key: string) => {
          const child = this.objects[key];
          if (this.productType === SockType.SOCK) {
            this.texture.encoding = THREE.sRGBEncoding;
            const origin = new THREE.MeshPhysicalMaterial({});
            const properties = {};
            for (const key in origin) {
              if (child.material.hasOwnProperty(key)) {
                if (key !== 'type') {
                  Reflect.set(properties, key, child.material[key]);
                }
              }
            }
            child.material = new THREE.MeshPhysicalMaterial({
              ...properties,
              map: this.texture,
              bumpMap: this.bumpTexture,
              bumpScale: 0.2,
            });
            child.material.needsUpdate = true;
          } else if (
            this.productType === SockType.BOXER
            || this.productType === SockType.BOXER015Z
          ) {
            switch (child.name) {
              case '顶_03':
                child.material = new THREE.MeshBasicMaterial({
                  map: this.texture,
                });
                break;
              case '缝纫线':
                child.material = new THREE.MeshBasicMaterial({
                  map: this.annexTexture.sewing,
                  transparent: true,
                });
                break;
              case '左边线':
              case '右边线':
                child.material = new THREE.MeshBasicMaterial({
                  map: this.annexTexture.dotted,
                  transparent: true,
                });
                break;
              case '顶_后':
                child.material = new THREE.MeshBasicMaterial({
                  color: 0xffffff,
                });
                break;
              default:
                child.material = new THREE.MeshPhysicalMaterial({
                  map: this.texture,
                  bumpMap: this.bumpTexture,
                  bumpScale: 0.8,
                });
                break;
            }
            child.material.needsUpdate = true;
          } else if (this.productType === SockType.KYBOXER) {
            switch (child.name) {
              case '内部':
                child.material = new THREE.MeshPhysicalMaterial({
                  color: 0xffffff,
                  bumpMap: this.bumpTexture,
                  bumpScale: 0.7,
                });
                break;
              case '顶部':
                child.material = new THREE.MeshBasicMaterial({
                  map: this.texture,
                });
                break;
              case '外_2':
                child.material = new THREE.MeshBasicMaterial({
                  map: this.annexTexture.sewing,
                  transparent: true,
                });
                break;
              case '顶部_后':
                child.material = new THREE.MeshBasicMaterial({
                  color: 0xffffff,
                });
                break;
              default:
                child.material = new THREE.MeshPhysicalMaterial({
                  map: this.texture,
                  bumpMap: this.bumpTexture,
                  bumpScale: 0.7,
                });
                break;
            }
          } else if (
            this.productType === SockType.BOXER435
            || this.productType === SockType.BOXER395Z
          ) {
            switch (child.name) {
              case 'xian1':
                child.material = new THREE.MeshBasicMaterial({
                  map: this.annexTexture.sewing,
                  transparent: true,
                });
                break;
              default:
                child.material = new THREE.MeshPhysicalMaterial({
                  map: this.texture,
                  bumpMap: this.bumpTexture,
                  bumpScale: 0.8,
                });
                break;
            }
          }
        });
        this.colorNames.forEach((item: Zen3dColorName) => {
          const child = this.objects[item.name];
          // child.material.color.set(item.color);
          child.material = new THREE.MeshPhysicalMaterial({
            color: new THREE.Color(item.color),
            bumpMap: this.bumpTexture,
            bumpScale: 1,
          });
          child.material.needsUpdate = true;
        });
        this.texture.needsUpdate = true;
        resolve();
      }
      image.src = source;
    });
  }

  /** 导出图片 */
  public exportImage() {
    const position = sockConfig[this.productType].initialCamera;
    const cameraLookAt = sockConfig[this.productType].cameraLookat;
    // 创建导出相机
    const cameraExport = this.camera.clone();
    cameraExport.position.set(position.x, position.y, position.z);
    cameraExport.up.set(0, 1, 0);
    // 创建导出场景
    const sceneExport = this.scene.clone();
    sceneExport.background = null;
    // 创建导出渲染器
    const rendererExport = cloneDeep(this.renderer);
    sceneExport.children.find(item => item.type === 'Group')!.scale.multiplyScalar(1.8);
    // 因为这里改变渲染 DOM 的宽高，需要保存一个原始的宽高，导出完成后设置回去
    const origin = new THREE.Vector2();
    this.renderer.getSize(origin);
    rendererExport.setClearAlpha(0);
    cameraExport.aspect = 1;
    cameraExport.updateProjectionMatrix();
    rendererExport.setSize(300, 300, false);
    // 袜子角度和控制器有关
    const orbitControls = new OrbitControls(cameraExport, rendererExport.domElement);
    orbitControls.target.set(...cameraLookAt);
    orbitControls.update();
    rendererExport.render(sceneExport, cameraExport);
    const image = rendererExport.domElement.toDataURL('image/png');
    this.renderer.setSize(origin.width, origin.height);
    rendererExport.dispose();
    orbitControls.dispose();
    return image;
  }
}

export default Sock3d;
